import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const map = {
    approved: "badge-paid",
    pending: "badge-due-today",
    rejected: "badge-overdue",
  };
  return (
    <span
      data-testid={`status-${status}`}
      className={`inline-flex items-center text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-sm ${map[status] || "badge-unpaid"}`}
    >
      {status.toUpperCase()}
    </span>
  );
}

export default function MyPayments() {
  const [rows, setRows] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef(null);

  const load = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get("/user/payments");
      setRows(data);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    intervalRef.current = setInterval(() => load(true), 15000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const manualRefresh = async () => {
    await load();
    toast.success("Refreshed");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">My Payments</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {rows.length} payment{rows.length !== 1 && "s"} submitted · auto-refresh on
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={manualRefresh}
          disabled={refreshing}
          data-testid="refresh-payments"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="glass-card rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Submitted</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>TXN ID</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                    No payments yet.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`pay-${r.id}`}>
                  <TableCell className="font-mono text-xs">
                    {r.submitted_at?.slice(0, 10)}
                  </TableCell>
                  <TableCell>{r.plan?.name || "—"}</TableCell>
                  <TableCell className="uppercase text-xs font-semibold">{r.method}</TableCell>
                  <TableCell className="font-mono text-xs">{r.txn_id}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">
                    ৳{Number(r.amount).toFixed(0)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
